/**
 * Store control endpoints (`/api/v1/control/*`, docs/plans/store_control_v1.md "Client"). Typed with the
 * hand-written shapes of control_types.ts until the generated OpenAPI operations exist.
 */
import type {
  ControlCompareMode,
  ControlCompareQuery,
  ControlCompareResult,
  ControlEventDetail,
  ControlEventPage,
  ControlEventsQuery,
  ControlFalsePositiveResult,
  ControlRangeQuery,
  ControlSummary,
  ServiceQualityResult,
} from './control_types';
import type { ApiClient, QueryParameterValue } from './http_client';

function rangeParameters(rangeQuery: ControlRangeQuery): Record<string, QueryParameterValue> {
  return {
    from: rangeQuery.from,
    to: rangeQuery.to,
    store_ids: rangeQuery.storeIds === undefined ? undefined : [...rangeQuery.storeIds],
  };
}

export function createControlEndpoints(apiClient: ApiClient) {
  return {
    summary: (rangeQuery: ControlRangeQuery, compare: ControlCompareMode, signal?: AbortSignal) =>
      apiClient.get<ControlSummary>('/control/summary', {
        signal,
        query: { ...rangeParameters(rangeQuery), compare },
      }),
    events: (eventsQuery: ControlEventsQuery, signal?: AbortSignal) =>
      apiClient.get<ControlEventPage>('/control/events', {
        signal,
        query: {
          ...rangeParameters(eventsQuery),
          states: eventsQuery.states === undefined ? undefined : [...eventsQuery.states],
          zone_ids: eventsQuery.zoneIds === undefined ? undefined : [...eventsQuery.zoneIds],
          open_only: eventsQuery.openOnly === true ? true : undefined,
          cursor: eventsQuery.cursor ?? undefined,
          limit: eventsQuery.limit,
        },
      }),
    event: (eventId: string, signal?: AbortSignal) =>
      apiClient.get<ControlEventDetail>(`/control/events/${encodeURIComponent(eventId)}`, { signal }),
    markFalsePositive: (eventId: string) =>
      apiClient.post<ControlFalsePositiveResult>(
        `/control/events/${encodeURIComponent(eventId)}/false-positive`,
      ),
    compare: (compareQuery: ControlCompareQuery, signal?: AbortSignal) =>
      apiClient.get<ControlCompareResult>('/control/compare', {
        signal,
        query: {
          ...rangeParameters(compareQuery),
          metric: compareQuery.metric,
          state: compareQuery.state ?? undefined,
        },
      }),
    serviceQuality: (rangeQuery: ControlRangeQuery, compare: ControlCompareMode, signal?: AbortSignal) =>
      apiClient.get<ServiceQualityResult>('/control/service-quality', {
        signal,
        query: { ...rangeParameters(rangeQuery), compare },
      }),
  };
}

export type ControlEndpoints = ReturnType<typeof createControlEndpoints>;
